import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { TrendingUp, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { z } from 'zod';
import { passwordSchema } from '@/utils/passwordValidation';

const resetPasswordSchema = z.object({
  password: passwordSchema,
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [passwordData, setPasswordData] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isValidToken, setIsValidToken] = useState(false);

  useEffect(() => {
    // Verificar se há token na URL
    const token = searchParams.get('token');
    
    if (!token) {
      toast.error('Link de recuperação inválido ou expirado');
      setTimeout(() => navigate('/auth'), 3000);
      return;
    }

    setIsValidToken(true);
  }, [searchParams, navigate]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      const validatedData = resetPasswordSchema.parse(passwordData);
      const token = searchParams.get('token');

      if (!token) {
        toast.error('Token inválido');
        return;
      }

      const { data, error } = await supabase.functions.invoke('validate-reset-token', {
        body: {
          token: token,
          newPassword: validatedData.password
        }
      });

      if (error) {
        toast.error('Token inválido ou expirado');
        return;
      }

      if (!data.success) {
        toast.error(data.error || 'Erro ao redefinir senha');
        return;
      }

      toast.success('Senha redefinida com sucesso!');
      
      // Redirecionar para login após redefinir senha
      setTimeout(() => navigate('/auth'), 1500);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast.error('Erro ao redefinir senha');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-center gap-2 text-primary">
              <TrendingUp className="h-8 w-8" />
              <span className="text-2xl font-bold">Dashi</span>
            </div>
            <div className="text-center">
              <CardTitle className="text-2xl">Link Inválido</CardTitle>
              <CardDescription>Este link de recuperação é inválido ou expirou</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground mb-4">
              Você será redirecionado para a página de login...
            </p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/auth')}
              className="w-full gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center gap-2 text-primary">
            <TrendingUp className="h-8 w-8" />
            <span className="text-2xl font-bold">Dashi</span>
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl">Redefinir Senha</CardTitle>
            <CardDescription>Digite sua nova senha</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Nova Senha</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••"
                value={passwordData.password}
                onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                required
              />
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
              />
              {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
            </div>
            
            {/* Requisitos de Senha */}
            <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-md text-sm">
              <p className="font-medium text-blue-900 dark:text-blue-100 mb-2">A senha deve conter:</p>
              <ul className="space-y-1 text-blue-700 dark:text-blue-300">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Mínimo de 6 caracteres
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Pelo menos 1 letra maiúscula
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Pelo menos 1 letra minúscula
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Pelo menos 1 número
                </li>
              </ul>
            </div>
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Redefinindo...' : 'Redefinir Senha'}
            </Button>
          </form>
          
          <div className="mt-6 flex justify-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/auth')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar para Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
